/**
 * Application root.
 *
 * After audit item 28, `App.tsx` holds only the boot wiring and the provider
 * tree. The respondent state machine lives in `components/InterviewFlow`, the
 * admin DB-write surface in `components/AdminSetupPage`, and the route table in
 * `components/AppRouter`.
 *
 * @module App
 */

import React, { useLayoutEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { SupabaseProvider } from './lib/SupabaseProvider';
import { OrgCoreProvider } from './lib/OrgCoreProvider';
import { BillingProvider } from './lib/BillingProvider';
import { WorkspacesProvider } from './lib/WorkspacesProvider';
import { ReleaseFlagsProvider } from './lib/release-flags';
import { PostHogOrganizationBridge } from './lib/telemetry/posthog/PostHogOrganizationBridge';
import { ThemeProvider } from './lib/ThemeProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppRouter } from './components/AppRouter';

// Re-export the respondent flow so existing deep imports (and tests) that
// reference `InterviewFlow` from the app root keep resolving after the split.
export { InterviewFlow } from './components/InterviewFlow';

// Root App Component
const App: React.FC = () => {
  // Remove the inline boot loader (index.html) the moment React mounts and
  // BEFORE we paint our first frame. useLayoutEffect runs synchronously
  // after DOM mutation but before the browser paints, so the React loader
  // (BrandedLineLoader, when shown) lands on screen WITHOUT the boot loader
  // overlapping it. This eliminates the dual-loader visual jolt.
  useLayoutEffect(() => {
    const shell = document.getElementById('boot-loader');
    if (shell) shell.remove();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <SupabaseProvider>
          {/*
            Org context is split into three providers (audit item 26). They nest
            in dependency order: BillingProvider and WorkspacesProvider read the
            active org from OrgCoreProvider, so core must be outermost. The legacy
            `useOrg()` adapter recombines all three for existing consumers.
          */}
          <OrgCoreProvider>
            <BillingProvider>
              <PostHogOrganizationBridge />
              <ReleaseFlagsProvider>
                <WorkspacesProvider>
                  <BrowserRouter>
                    <AppRouter />
                  </BrowserRouter>
                </WorkspacesProvider>
              </ReleaseFlagsProvider>
            </BillingProvider>
          </OrgCoreProvider>
        </SupabaseProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
